import { useCallback } from 'react';

import { assertUtil } from '@pukka/common/utils';
import { addDays, addMonths, addYears, isAfter, isBefore, isSameDay, subDays, subMonths, subYears } from 'date-fns';
import { FaChevronRight, FaChevronLeft, FaRegCalendarAlt } from 'react-icons/fa';

import { Button } from '../Button';
import { Popover } from '../Popover';

import { Modal } from './Modal';
import { clampTo, DatePickerProps, getDifferences, getDisplayDate, getValidDate, MAX_DATE, MIN_DATE } from './utils';

function DatePicker({
  size,
  allDay,
  timePicker,
  rangePicker,
  dates,
  startDate,
  endDate,
  className,
  disabled = false,
  onChangeDate,
}: DatePickerProps) {
  const disabledClass = 'bg-neutral-100 text-neutral-300';
  const start = getValidDate(startDate, allDay ? 0 : undefined);
  const end = rangePicker ? getValidDate(endDate, allDay ? 24 : undefined) : undefined;
  const minDate = dates?.[0] ?? MIN_DATE;
  const maxDate = dates?.[1] ?? MAX_DATE;

  const clampDate = useCallback(
    <T extends Date | undefined>(_start: Date, _end: T) => {
      if (_end) {
        return [
          clampTo(_start, {
            start: minDate,
            end: maxDate,
          }),
          clampTo(_end, {
            start: minDate,
            end: maxDate,
          }),
        ] as undefined extends T ? [Date] : [Date, Date];
      }

      return [
        clampTo(_start, {
          start: minDate,
          end: maxDate,
        }),
      ] as undefined extends T ? [Date] : [Date, Date];
    },
    [maxDate, minDate],
  );

  const onSelectBefore = useCallback(() => {
    if (rangePicker) {
      assertUtil.assertIsDate(end);

      const { diffInDays, diffInMonths, diffInYears } = getDifferences(start, end, 1);

      switch (true) {
        case diffInYears > 0:
          onChangeDate?.(...clampDate(subYears(start, diffInYears), subYears(end, diffInYears)), !!allDay);
          break;
        case diffInMonths > 0:
          onChangeDate?.(...clampDate(subMonths(start, diffInMonths), subMonths(end, diffInMonths)), !!allDay);
          break;
        default:
          onChangeDate?.(...clampDate(subDays(start, diffInDays), subDays(end, diffInDays)), !!allDay);
      }
    } else {
      onChangeDate?.(...clampDate(subDays(start, 1), undefined), !!allDay);
    }
  }, [rangePicker, start, end, allDay, clampDate, onChangeDate]);

  const onSelectAfter = useCallback(() => {
    if (rangePicker) {
      assertUtil.assertIsDate(end);

      const { diffInDays, diffInMonths, diffInYears } = getDifferences(start, end, 1);

      switch (true) {
        case diffInYears > 0:
          onChangeDate?.(...clampDate(addYears(start, diffInYears), addYears(end, diffInYears)), !!allDay);
          break;
        case diffInMonths > 0:
          onChangeDate?.(...clampDate(addMonths(start, diffInMonths), addMonths(end, diffInMonths)), !!allDay);
          break;
        default:
          onChangeDate?.(...clampDate(addDays(start, diffInDays), addDays(end, diffInDays)), !!allDay);
      }
    } else {
      onChangeDate?.(...clampDate(addDays(start, 1), undefined), !!allDay);
    }
  }, [rangePicker, start, end, allDay, clampDate, onChangeDate]);

  return (
    <Button.Group className={className}>
      <Button
        aria-label="Previous Date(s)"
        variant="secondary"
        size={size}
        disabled={
          disabled ||
          (rangePicker
            ? (assertUtil.assertIsDate(end),
              isAfter(minDate, end) || isSameDay(minDate, end) || isAfter(minDate, start) || isSameDay(minDate, start))
            : isAfter(minDate, start) || isSameDay(minDate, start))
        }
        className={disabled ? disabledClass : ''}
        onClick={onSelectBefore}
      >
        <FaChevronLeft className="my-1 h-3" />
      </Button>
      <Popover
        unwrap
        trigger={
          <Button
            data-testid="date-picker-btn"
            size={size}
            variant="secondary"
            disabled={disabled}
            className={disabled ? disabledClass : ''}
          >
            {getDisplayDate(start, end ?? start, allDay)} <FaRegCalendarAlt size={16} className="-mt-0.5 ml-2 opacity-80" />
          </Button>
        }
      >
        <Popover.Content sideOffset={5} align="start" alignOffset={-39}>
          {rangePicker ? (
            <Modal
              rangePicker
              timePicker={timePicker}
              allDay={allDay}
              dates={dates}
              startDate={start}
              endDate={end}
              onChangeDate={onChangeDate}
            />
          ) : (
            <Modal timePicker={timePicker} allDay={allDay} dates={dates} startDate={start} onChangeDate={onChangeDate} />
          )}
        </Popover.Content>
      </Popover>
      <Button
        aria-label="Next Date(s)"
        variant="secondary"
        size={size}
        disabled={
          disabled ||
          (rangePicker
            ? (assertUtil.assertIsDate(end),
              isBefore(maxDate, end) || isSameDay(maxDate, end) || isBefore(maxDate, start) || isSameDay(maxDate, start))
            : isBefore(maxDate, start) || isSameDay(maxDate, start))
        }
        className={disabled ? disabledClass : ''}
        onClick={onSelectAfter}
      >
        <FaChevronRight className="my-1 h-3" />
      </Button>
    </Button.Group>
  );
}

export { DatePicker };
